import type { Reel } from './reelTypes';

/**
 * PLAT-11052 — drops the names a reel is carrying for Sections that have left it.
 *
 * `clipJson.reel` holds only what the timeline cannot: names and chapter
 * boundaries. Every write to it *adds* — a rename, an insert's seeded names —
 * and nothing has ever removed, because membership is **read** off the timeline
 * rather than repaired on write (DE12, PLAT-11052 DS1). That is the right rule
 * for rendering: a name left behind for a Section that is gone is harmless on
 * screen, because nothing looks it up. It is the wrong rule for storage, where
 * the map becomes one entry per Section the clip has *ever* held and grows with
 * every edit — a removed Section, an undone insert, a re-cut passage.
 *
 * So the save path prunes, which is where the live Sections are already in hand.
 *
 * **Chapters are deliberately left alone.** A chapter whose `firstSectionId` has
 * left the reel, and which has no `afterSectionId` to fall back on, looks like
 * the same dead weight — but `deriveReelChapterGroups` reads exactly that shape:
 * it is the chapter that *advances* onto whichever Section now opens the reel,
 * which is how the opening chapter keeps its title when the Section it started
 * at is removed (PLAT-11055 DS3). Dropping it here would trade a bounded amount
 * of storage — a chapter exists only where a creator made one — for a title lost
 * on the next reload. Names have no such second life: nothing re-homes one.
 *
 * Pure and non-mutating, and returns the **same** reel when there is nothing to
 * drop, so a save that changed nothing writes no new object.
 */
export function pruneReelSectionNames(
  reel: Reel | undefined,
  liveSectionIds: Iterable<string>,
): Reel | undefined {
  if (!reel?.sections) return reel;

  const live = new Set(liveSectionIds);
  const dead = Object.keys(reel.sections).filter(id => !live.has(id));
  if (dead.length === 0) return reel;

  const sections = { ...reel.sections };
  for (const id of dead) delete sections[id];
  return { ...reel, sections };
}
